import { FilterItem, FilterItemsGroup } from "@app/resource-filters/filter-items";
import { FilteredResource } from "@app/resources";
import { FilterItemsGroupOperation } from "@app/resource-filters/common";
import { VolumeReportsProject, VolumeReportsState } from "@app/reports/volume-reports/import/interfaces";
import { CreateExceptionsProject } from "@app/exceptions/interfaces";
import ServiceBase from "@app/shared/serviceBase";

/** Handles communication across the wire when retrieving and interacting with projects. */
export default class ResourceFiltrationService extends ServiceBase {

  /* ****************
   * Filter Items/Filtered Projects
   * **************** */

  /**
   * Gets the filter-items given the filterNamespace.
   * @param filterNamespace [Optional] Provide no namespace to get all filter-items
   */
  public async getFilterItems(filterNamespace?: string, succeededOnlyResources?: boolean, publishedOnly?: boolean, removeExcludedInVolumeActionProjects?: boolean, zipSummariesOnly?: boolean, zipProjectsOnly?: boolean): Promise<FilterItem[]> {

    const model: GetFilterItemsRequestModel = {
      excludeNamespacePaths: ["§LinesOfBusiness"],
      excludeSubsetDupeItems: false,
      excludeZeroItems: false,
      path: filterNamespace !== undefined ? filterNamespace : "",
      publishedOnly: publishedOnly !== undefined ? publishedOnly : false,
      removeExcludedInVolumeActionProjects: removeExcludedInVolumeActionProjects !== undefined ? removeExcludedInVolumeActionProjects : false,
      searchText: null,
      succeededOnlyResources: succeededOnlyResources !== undefined ? succeededOnlyResources : false,
      zipProjectsOnly: zipProjectsOnly !== undefined ? zipProjectsOnly : false,
      zipSummariesOnly: zipSummariesOnly,
    };

    return (await this.makePost<FilterItem[]>(`resourcefiltration/filters`, model))
      .filter((fi: FilterItem) => fi.resourceCount > 0);
  }

  /**
   * Gets the filter-items given the filterGroups and filterNamespace.
   * @param filterGroups The filter-groups that provide the scope for which to get the filter-items.
   * @param filterNamespace [Optional] Provide no namespace to get all filter-items for the given filterGroups.
   */
  public async getFilterItemsInScope(
    filterGroups: FilterItemsGroup[],
    filterNamespace?: string,
    succeededOnlyResources?: boolean,
    publishedOnly?: boolean,
    removeExcludedInVolumeActionProjects?: boolean,
    zipSummariesOnly?: boolean,
    zipProjectsOnly?: boolean,
  ): Promise<FilterItem[]> {
    if (!filterGroups || filterGroups.length === 0) {
      return [];
    }

    const model: GetFilterItemsInScopeRequestModel = {
      excludeFilterItems: false,
      excludeNamespacePaths: ["§LinesOfBusiness"],
      excludeSubsetDupeItems: false,
      excludeZeroItems: false,
      filterItemGroups: this.toFilterItemsGroupModel(filterGroups),
      path: filterNamespace !== undefined ? filterNamespace : "",
      publishedOnly: publishedOnly !== undefined ? publishedOnly : false,
      removeExcludedInVolumeActionProjects: removeExcludedInVolumeActionProjects !== undefined ? removeExcludedInVolumeActionProjects : false,
      searchText: null,
      succeededOnlyResources: succeededOnlyResources !== undefined ? succeededOnlyResources : false,
      zipProjectsOnly: zipProjectsOnly !== undefined ? zipProjectsOnly : false,
      zipSummariesOnly: zipSummariesOnly,
    };

    return (await this.makePost<FilterItem[]>(`resourcefiltration/filters/in-scope`, model))
      .filter((fi: FilterItem) => fi.resourceCount > 0);
  }

  /**
   * Gets the project-ids given the filterGroups and filterNamespace.
   * @param filterGroups [Optional] The filter-groups that provide the scope for which to get the project-ids. Provide no filter-groups to get all project-ids.
   */
  public async getFilteredProjectResources(filterGroups?: FilterItemsGroup[]): Promise<FilteredResource[]> {
    return await this.makePost<FilteredResource[]>(`resourcefiltration/filters/resources`, this.toFilterItemsGroupModel(filterGroups));
  }

  /**
   * Gets the states and counties of the current user's existing projects
   * @returns An array of filter items, each of which is extended with another array of child filter items
   */
  public getStatesAndCounties(publishedOnly: boolean, zipSummariesOnly: boolean, zipProjectsOnly?: boolean): Promise<VolumeReportsState[]> {
    const query = new URLSearchParams({
      publishedOnly: publishedOnly.toString(),
      zipProjectsOnly: (zipProjectsOnly ?? false).toString(),
      zipSummariesOnly: zipSummariesOnly.toString(),
    });
    return this.makeGet<VolumeReportsState[]>(`resourcefiltration/service-areas?${query.toString()}`);
  }

  public getVolumeExceptionsProjects(filterItemGroups: FilterItemsGroupModel[]): Promise<CreateExceptionsProject[]> {
    return this.makePost(`resourcefiltration/filters/volume-exception-resources`, filterItemGroups);
  }

  public getVolumeReportingProjects(filterItemGroups: FilterItemsGroupModel[], publishedOnly: boolean, zipSummariesOnly: boolean, pciOnly: boolean, invalidNetworksOnly: boolean, validatedNetworksOnly: boolean, zipProjectsOnly?: boolean): Promise<VolumeReportsProject[]> {
    const query = new URLSearchParams({
      invalidNetworksOnly: invalidNetworksOnly.toString(),
      pciOnly: pciOnly.toString(),
      publishedOnly: publishedOnly.toString(),
      validatedNetworksOnly: validatedNetworksOnly.toString(),
      zipProjectsOnly: (zipProjectsOnly ?? false).toString(),
      zipSummariesOnly: zipSummariesOnly.toString(),
    });
    return this.makePost(`resourcefiltration/filters/volume-report-resources?${query.toString()}`, filterItemGroups);
  }

  public getAccuracyReportingProjects(filterItemGroups: FilterItemsGroupModel[], publishedOnly: boolean, zipSummariesOnly: boolean): Promise<VolumeReportsProject[]> {
    const query = new URLSearchParams({
      publishedOnly: publishedOnly.toString(),
      zipSummariesOnly: zipSummariesOnly.toString(),
    });
    return this.makePost(`resourcefiltration/filters/accuracy-report-resources?${query.toString()}`, filterItemGroups);
  }

  public hasPciReportingOption(): Promise<boolean> {
    return this.makeGet<boolean>(`resourcefiltration/filters/has-pci-reporting-option`);
  }

  // Used by the Enterprise Compliance Dashboard
  public async getTagsFilterOptions(): Promise<Partial<FilterItem>[]> {
    const body: GetFilterItemsRequestModel = {
      excludeSubsetDupeItems: false,
      excludeZeroItems: false,
      path: "§Tags",
      publishedOnly: false,
      removeExcludedInVolumeActionProjects: false,
      searchText: undefined,
      succeededOnlyResources: false,
      zipSummariesOnly: false,
    };

    return await this.makePost<FilterItem[]>("resourcefiltration/filters/all-tags?sortbynamespace=true", body);
  }

  /**
   * Transforms the given filter groups into one suitable for API usage.
   * @param filterGroups The set of filter groups to transform.
   */
  private toFilterItemsGroupModel(filterGroups?: FilterItemsGroup[]) {
    return (filterGroups || []).map((fi) => ({ filterItems: (fi.filters || []).map((x) => x.namespacePath), operation: fi.operation }) as FilterItemsGroupModel);
  }
}

// not to be confused with FilterItemsGroup
export interface FilterItemsGroupModel {
  filterItems: string[];
  operation: FilterItemsGroupOperation;
}

export interface GetFilterItemsRequestModel {
  excludeNamespacePaths?: string[];
  excludeSubsetDupeItems: boolean;
  excludeZeroItems: boolean;
  path: string;
  publishedOnly: boolean;
  searchText: string;
  succeededOnlyResources: boolean;
  removeExcludedInVolumeActionProjects: boolean;
  zipProjectsOnly?: boolean;
  zipSummariesOnly: boolean;
}

interface GetFilterItemsInScopeRequestModel extends GetFilterItemsRequestModel {
  excludeFilterItems: boolean;
  filterItemGroups: FilterItemsGroupModel[];
}
